<template>
  <div class="d-flex flex-column page-wrapper contact-information">
    <page-header>
      <span v-if="editMode">{{ $t("message.edit" )}}</span> {{ $t("message.jointApplicantContactInfo" )}}
    </page-header>
    <div class="container d-flex flex-column align-items-center page-container">
      <div class="d-flex flex-row justify-content-around steps-container">
        <div class="dot previous-dot"></div><!-- Identification -->
        <div class="dot previous-dot"></div><!-- Contact Info -->
        <div class="dot previous-dot"></div><!-- Other Info -->
        <div class="dot previous-dot" v-if="jointBorrower == true"/><!-- Joint Identification -->
        <div class="dot current-dot" v-if="jointBorrower == true"/><!-- Joint Contact Info -->
        <div class="dot next-dot" v-if="jointBorrower == true"/><!-- Joint Other Info -->
        <div class="dot next-dot"></div><!-- Accounts -->
        <div class="dot next-dot"></div><!-- Funding -->
        <div class="dot next-dot"></div><!-- Review -->
      </div>
      <p class="explanation-text" v-if="!processing">
        <strong>Check to make sure all information is correct.</strong> Add any additional information we need to get your membership established.
      </p>
      <div class="d-flex flex-column justify-content-start align-items-start form-container" v-if="!processing">
        <b-form @submit.prevent="submit" novalidate>
          <b-form-group class="form-label" label="Street Address">
            <b-form-input type="text" :id="$id('address1')" v-model="address1"/>        
            <div class="field-error-message" v-if="!$v.address1.required && $v.address1.$dirty">Please enter your Street Address</div>        
          </b-form-group>
          <b-form-group class="form-label" label="Address Line 2">
            <b-form-input type="text" :id="$id('address2')" v-model="address2"/>        
          </b-form-group>
          <b-form-group class="form-label" label="City">
            <b-form-input type="text" v-model="city"/>        
            <div class="field-error-message" :id="$id('city')" v-if="!$v.city.required && $v.city.$dirty">Please enter your City</div>        
          </b-form-group>
          <b-form-group class="form-label" label="State">
            <b-form-select v-model="state" :id="$id('state')" :options="stateOptions" class="form-control"></b-form-select>
            <div class="field-error-message" v-if="!$v.state.required && $v.state.$dirty">Please enter your State</div>        
          </b-form-group>
          <b-form-group class="form-label" label="Zip Code">
            <b-form-input type="tel" :id="$id('zipCode')" v-model="zipCode" v-mask="'#####', '#####-####'"/>        
            <div class="field-error-message" v-if="!$v.zipCode.required && $v.zipCode.$dirty">Please enter your Zip Code</div>        
            <div class="field-error-message" v-if="!$v.zipCode.zipCodeValidation && $v.zipCode.minLength && $v.zipCode.$dirty && $v.state.required">This Zip Code is not valid for the selected state</div>        
            <div class="field-error-message" v-if="!$v.zipCode.minLength && $v.zipCode.$dirty">Please enter a valid Zip Code</div>        
          </b-form-group>
          <b-form-group class="form-label" label="How long have you lived at this address?">
            <div class="d-flex flex-row">
              <b-input-group append="Years">
                <b-form-input type="number" :id="$id('yearsAtAddress')" v-model="yearsAtAddress" min="0" class="width-50" />
              </b-input-group>
              <b-input-group append="Months">
                <b-form-input type="number" :id="$id('monthsAtAddress')" v-model="monthsAtAddress" min="0" max="11" class="width-50" />
              </b-input-group>  
            </div>
            <div class="field-error-message" v-if="!$v.yearsAtAddress.required && $v.yearsAtAddress.$dirty">Please enter the number of years you've lived at this address</div>        
            <div class="field-error-message" v-if="!$v.monthsAtAddress.required && $v.monthsAtAddress.$dirty">Please enter the number of months you've lived at this address</div>        
            <div class="field-error-message" v-if="!$v.monthsAtAddress.minValue && $v.monthsAtAddress.$dirty">The number of months must be between 0 and 11</div>     
            <div class="field-error-message" v-if="!$v.monthsAtAddress.maxValue && $v.monthsAtAddress.$dirty">The number of months must be between 0 and 11</div>   
          </b-form-group>
          <b-form-group class="form-label" label="Do you own your home or rent?">
            <b-button-group vertical class="btn-width" :id="$id('housingtype-btngroup')">                        
              <b-button variant="outline-primary" :id="$id('own')" :pressed="housingType === 'O'" @click="housingType = 'O'">Own</b-button>                                                
              <b-button variant="outline-primary" :id="$id('rent')" :pressed="housingType === 'R'" @click="housingType = 'R'">Rent</b-button>                                                
            </b-button-group>   
            <div class="field-error-message" v-if="!$v.housingType.required && $v.housingType.$dirty">Please let us know if you own your home or rent</div>        
          </b-form-group>
          <div v-if="housingType" class="more-info-section">
            <b-form-group class="form-label">
              <label>What is your monthly <span v-if="housingType === 'O'">mortgage</span><span v-else-if="housingType === 'R'">rent</span> payment?</label>
              <b-input-group>
                <template #prepend>
                  <b-input-group-text><b>$</b></b-input-group-text>
                </template>
                <currency-input class="form-control" :id="$id('monthlyPayment')" inputmode="decimal" v-model="monthlyPayment" currency="USD"/>  
              </b-input-group>    
              <div class="field-error-message" v-if="!$v.monthlyPayment.required && $v.monthlyPayment.$dirty">Please let us know what your monthly payment is</div>        
            </b-form-group>
            <b-form-group v-if="housingType === 'O'" class="form-label">
              <label class="form-label" >What is the <span>current value of your home</span>?</label>
              <b-input-group>
                <template #prepend>
                  <b-input-group-text><b>$</b></b-input-group-text>
                </template>
                <currency-input class="form-control" :id="$id('homeValue')" inputmode="decimal" v-model="homeValue" currency="USD"/> 
              </b-input-group>
              <div class="field-error-message" v-if="!$v.homeValue.required && $v.homeValue.$dirty">Please let us know what your current home value is</div>         
            </b-form-group>
          </div>
          <b-form-group class="margin-top-15" v-for="(item, index) in $v.previousAddresses.$each.$iter" :key="index">
            <label class="form-label more-info-section-header">Additional Address Information</label>
            <div class="more-info-section previous-address">
              <b-form-group class="form-label" label="Street Address">
                <b-form-input type="text" :id="$id('address1-'+index)" v-model="item.$model.address1"/>        
                <div class="field-error-message" v-if="!item.address1.required && item.address1.$dirty">Please enter your Street Address</div>        
              </b-form-group>
              <b-form-group class="form-label" label="Address Line 2">
                <b-form-input type="text" :id="$id('address2-'+index)" v-model="item.$model.address2"/>        
              </b-form-group>
              <b-form-group class="form-label" label="City">
                <b-form-input type="text" :id="$id('city-'+index)" v-model="item.$model.city"/>        
                <div class="field-error-message" v-if="!item.city.required && item.city.$dirty">Please enter your City</div>        
              </b-form-group>
              <b-form-group class="form-label" label="State">
                <b-form-select class="form-control" :id="$id('state-'+index)" v-model="item.$model.state" :options="stateOptions"></b-form-select>
                <div class="field-error-message" v-if="!item.state.required && item.state.$dirty">Please enter your State</div>        
              </b-form-group>
              <b-form-group class="form-label" label="Zip Code">
                <b-form-input type="tel" :id="$id('zipCode-'+index)" v-model="item.$model.zipCode" v-mask="'#####', '#####-####'"/>        
                <div class="field-error-message" v-if="!item.zipCode.required && item.zipCode.$dirty">Please enter your Zip Code</div>        
                <div class="field-error-message" v-if="!item.zipCode.zipCodeValidation && item.zipCode.minLength && item.zipCode.$dirty && item.state.required">This Zip Code is not valid for the selected state</div>        
                <div class="field-error-message" v-if="!item.zipCode.minLength && item.zipCode.$dirty">Please enter a valid Zip Code</div>        
              </b-form-group>
              <b-form-group class="form-label" label="How long have you lived at this address?">
                <div class="d-flex flex-row">
                  <b-input-group append="Years">
                    <b-form-input type="number" :id="$id('yearsAtAddress-'+index)" v-model="item.$model.yearsAtAddress" min="0" class="width-50" />
                  </b-input-group>
                  <b-input-group append="Months">
                    <b-form-input type="number" :id="$id('monthsAtAddress-'+index)" v-model="item.$model.monthsAtAddress" min="0" max="11" class="width-50" />
                  </b-input-group>  
                </div>
                <div class="field-error-message" v-if="!item.yearsAtAddress.required && item.yearsAtAddress.$dirty">Please enter the number of years you've lived at this address</div>        
                <div class="field-error-message" v-if="!item.monthsAtAddress.required && item.monthsAtAddress.$dirty">Please enter the number of months you've lived at this address</div>        
                <div class="field-error-message" v-if="!item.monthsAtAddress.minValue && item.monthsAtAddress.$dirty">The number of months must be between 0 and 11</div>     
                <div class="field-error-message" v-if="!item.monthsAtAddress.maxValue && item.monthsAtAddress.$dirty">The number of months must be between 0 and 11</div>
              </b-form-group>
              <b-button variant="dark" :id="$id('removeHousing-btn-'+index)" @click.prevent="removeAdditionalHousing(index)" class="d-flex justify-content-center align-items-center remove-btn">
                <font-awesome-icon :icon="['fas', 'times']"></font-awesome-icon>
                <span class="margin-left-10">{{ $t("message.remove" )}}</span>
              </b-button>
            </div>
          </b-form-group>
          <label v-if="additionalAddressRequired" class="form-label field-error-message margin-top-0" :class="{ 'margin-top-15': previousAddresses.length == 0 }">You do not have enough housing history for this loan. Click "Add Previous Housing Info" to add additional housing.</label>
          <b-button v-if="additionalAddressRequired" :id="$id('addHousing-btn')" variant="primary" @click="addAdditionalHousing()" class="btn-width margin-bottom-15">
              <font-awesome-icon :icon="['fas', 'plus']"></font-awesome-icon> 
              <span class="margin-left-10">Add Previous Housing Info</span>
          </b-button>
          <b-form-group class="form-label" label="Do you have a separate mailing address?">
            <div class="switch-btn-container">
              <label :for="$id('separateMailingSwitch')" class="switch-btn-left-label">No</label>
              <b-form-checkbox class="switch-btn" v-model="separateMailingAddress" :id="$id('separateMailingSwitch')" switch size="lg"></b-form-checkbox>
              <label :for="$id('separateMailingSwitch')" class="switch-btn-right-label">Yes</label>
            </div>
            <div class="field-error-message" v-if="!$v.separateMailingAddress.required && $v.separateMailingAddress.$dirty">Please tell us whether you have a separate mailing address.</div>
          </b-form-group>
          <b-form-group v-if="separateMailingAddress">
            <label class="form-label more-info-section-header">Mailing Address Information</label>
            <div class="more-info-section">
              <b-form-group class="form-label" label="Street Address">
                <b-form-input type="text" :id="$id('mailingAddress1')" v-model="mailingAddress1"/>        
                <div class="field-error-message" v-if="!$v.mailingAddress1.required && $v.mailingAddress1.$dirty">Please enter your Street Address</div>        
              </b-form-group>
              <b-form-group class="form-label" label="Address Line 2">
                <b-form-input type="text" :id="$id('mailingAddress2')" v-model="mailingAddress2"/>        
              </b-form-group>
              <b-form-group class="form-label" label="City">
                <b-form-input type="text" :id="$id('mailingCity')" v-model="mailingCity"/>        
                <div class="field-error-message" v-if="!$v.mailingCity.required && $v.mailingCity.$dirty">Please enter your City</div>        
              </b-form-group>
              <b-form-group class="form-label" label="State">
                <b-form-select class="form-control" :id="$id('mailingState')" v-model="mailingState" :options="stateOptions"></b-form-select>
                <div class="field-error-message" v-if="!$v.mailingState.required && $v.mailingState.$dirty">Please enter your State</div>        
              </b-form-group>
              <b-form-group class="form-label" label="Zip Code">
                <b-form-input type="tel" :id="$id('mailingZipCode')" v-model="mailingZipCode" v-mask="'#####', '#####-####'"/>        
                <div class="field-error-message" v-if="!$v.mailingZipCode.required && $v.mailingZipCode.$dirty">Please enter your Zip Code</div>        
                <div class="field-error-message" v-if="!$v.mailingZipCode.zipCodeValidation && $v.mailingZipCode.minLength && $v.mailingZipCode.$dirty && $v.mailingState.required">This Zip Code is not valid for the selected state</div>        
                <div class="field-error-message" v-if="!$v.mailingZipCode.minLength && $v.mailingZipCode.$dirty">Please enter a valid Zip Code</div>        
              </b-form-group>
            </div>
          </b-form-group>
          <b-form-group class="form-label" label="Phone Number">
            <b-input-group>
              <b-form-input type="tel" :id="$id('phoneNumber')" v-model="phoneNumber" v-mask="'(###)###-####'"/>
              <b-input-group-append>
                <b-dropdown variant="highlight" :id="$id('phoneTypes')" :no-caret="true">
                    <template #button-content>
                      <div class="min-width-70">
                        {{ phoneTypeText }}
                      </div>
                    </template>
                    <b-dropdown-item-button :pressed="option.value === phoneType" @click="updateApplicantPhoneType(option)" v-for="option in phoneTypes" :key="option.value">{{ option.name }}</b-dropdown-item-button>
                </b-dropdown>
              </b-input-group-append>
            </b-input-group>     
            <div class="field-error-message" v-if="!$v.phoneNumber.required && $v.phoneNumber.$dirty">Please enter your Phone Number</div>
            <div class="field-error-message" v-if="!$v.phoneNumber.minLength && $v.phoneNumber.$dirty">Please enter a valid Phone Number</div>   
            <div class="field-error-message" v-if="!$v.phoneType.required && $v.phoneType.$dirty">Please select the Phone Type</div>
          </b-form-group>
          <b-form-group class="form-label" label="Email Address">
            <b-input-group>
              <b-form-input type="email" :id="$id('emailAddress')" v-model="emailAddress" />
              <b-input-group-append>
                <b-dropdown variant="highlight" :id="$id('emailTypes')" :no-caret="true">
                    <template #button-content>
                      <div class="min-width-70">
                        {{ emailTypeText }}
                      </div>
                    </template>
                    <b-dropdown-item-button :pressed="option.value === emailType" @click="updateApplicantEmailType(option)" v-for="option in emailTypes" :key="option.value">{{ option.name }}</b-dropdown-item-button>
                </b-dropdown>
              </b-input-group-append>
            </b-input-group>            
            <div class="field-error-message" v-if="!$v.emailAddress.required && $v.emailAddress.$dirty">Please enter your Email Address</div>
            <div class="field-error-message" v-if="!$v.emailAddress.email && $v.emailAddress.$dirty">Please enter a valid Email Address</div>      
            <div class="field-error-message" v-if="!$v.emailType.required && $v.emailType.$dirty">Please select the Phone Type</div>
          </b-form-group>
          <b-form-group class="form-label" label="I prefer to be contacted by">
            <b-button-group vertical :id="$id('preferredMethodOfContact')" class="btn-width">                        
              <b-button variant="outline-primary" :pressed="preferredMethodOfContact === 'Phone'" @click="preferredMethodOfContact = 'Phone'">Phone</b-button>                                                
              <b-button variant="outline-primary" :pressed="preferredMethodOfContact === 'Email'" @click="preferredMethodOfContact = 'Email'">Email</b-button>                                                
            </b-button-group>
            <div class="field-error-message" v-if="!$v.preferredMethodOfContact.required && $v.preferredMethodOfContact.$dirty">Please tell us your preferred method of contact</div>        
          </b-form-group>
          <div class="d-flex flex-column justify-content-between align-items-center final-action-div">
              <b-button variant="secondary" type="submit" :id="$id('next')" class="btn btn-width">
                  <span v-if="!editMode">{{ $t("message.next" )}}</span>
                  <span v-if="editMode">{{ $t("message.saveAndContinue" )}}</span>
              </b-button>
              <b-button variant="light" @click="back()" :id="$id('back')" class="btn btn-width">
                  <span v-if="!editMode || editedIdentificationInfo">{{ $t("message.back" )}}</span>
                  <span v-if="editMode && !editedIdentificationInfo">{{ $t("message.cancel" )}}</span>
              </b-button>
          </div>
        </b-form>
      </div>
      <div class="d-flex flex-column align-items-center align-content-center justify-content-center form-container" v-if="processing">            
        <p class="processing-text"></p>
        <img src="@/assets/img/light/activity.gif" class="processing-img" />                           
      </div>    
    </div>
    <page-footer />
    <b-modal ref="errorModalRef" body-text-variant="dark" header-text-variant="dark" centered ok-only ok-variant="secondary" title="Missing Information">You must fill out the required fields.</b-modal>
    <b-modal ref="blocklistCheckRef" body-text-variant="dark" header-text-variant="dark" centered ok-only ok-variant="secondary" title="Unable to Apply">
      <span v-html="blocklistFoundMessage"></span>      
    </b-modal>
    <b-modal ref="ipDistanceCheckRef" body-text-variant="dark" header-text-variant="dark" centered ok-only ok-variant="secondary" title="Unable to Apply">
      <span v-html="ipDistanceRestrictionMessage"></span>      
    </b-modal>
    <b-modal ref="addressCheckRef" body-text-variant="dark" header-text-variant="dark" centered ok-only ok-variant="secondary" title="Unable to Apply">
      <span v-html="addressRestrictionFoundMessage "></span>      
    </b-modal>
    <b-modal ref="moreInfoRequiredModalRef" body-text-variant="dark" header-text-variant="dark" centered hide-header-close no-close-on-esc title="Notice" @cancel="cancel" cancel-variant="outline-secondary" ok-title="Continue" ok-variant="secondary" @ok="gotoOtherInfo">
      <p>Your contact information has changed and you may not be eligible for membership. We need a little more information to make sure you are able to join {{ creditUnionName }}.</p>
    </b-modal>
  </div>
</template>

<script>
import { required, requiredIf, email, minLength, minValue, maxValue, helpers } from "vuelidate/lib/validators";
import { mask } from 'vue-the-mask';
import { zipCodeValidation } from "@/validators/validators.js";
import { mapState, mapGetters, mapMutations, mapActions } from "vuex";
const alphaNum = helpers.regex('alphaNum', /^[a-zA-Z0-9 ' . -]*$/);
const alphaDash = helpers.regex('alphaDash', /^[a-zA-Z ' . -]*$/);
export default {
  name: "JointContactInformation",
  components: {
  },
  directives: {
    mask
  },
  props: {},
  mounted: function () {
    this.previousAddresses = this.jointPreviousAddresses;
    this.pageContent = this.getPageContent('Contact Information');
    if(this.editMode) {
      this.eligibilityBeforeEdit = this.currentState.jointEligibilityId && this.currentState.jointEligibilityId !== '';
      this.eligibilityId = '';
    }
    this.editedContactInfo = false;
  },
  data: function() {
      return {
        phoneTypes: [
            { name: 'Home', value: 'Home Phone'  },
            { name: 'Cell', value: 'Personal Cell' },
            { name: 'Business', value: 'Business Phone' },
        ],
        emailTypes: [
            { name: 'Personal', value: 'Personal Email' },
            { name: 'Business', value: 'Business Email' },
        ],
        previousAddresses: [],
        isPrimary: false,
        pageContent: [],
        processing: false,
        eligibilityBeforeEdit: null
      };
  },
  computed: {
    ...mapState([
        "editMode",
        "currentState",
        "stateOptions",
        "isJointApplication",
        "jointAddress",
        "jointPreviousAddresses",
        "jointMailingAddress",
        "jointApplicant",
        "jointPhone",
        "jointEmail",
        "minimumAddressHistoryMonths",
        'jointEligibilityId',
        'primaryEligibilityId',
        'applicantEligibilityRequirement',
        "blockListCheckRequired",
        "addressStateCheckRequired",
        "ipDistanceCheckRequired",
        'creditUnion'
    ]),
    ...mapGetters([
        'getPageContent',
        'jointAddressHistoryInMonths',
    ]),
    creditUnionName: {
        get() {
            return this.creditUnion.name;
        }
    },
    blocklistFoundMessage: {
      get() {        
        var text = this.pageContent.find(content => content.contentName === 'Blocklist Found');
        return text ? text.content : '';
      }
    },
    ipDistanceRestrictionMessage: {
      get() {        
        var text = this.pageContent.find(content => content.contentName === 'IP Distance Restriction Found');
        return text ? text.content : '';
      }
    },
    addressRestrictionFoundMessage: {
      get() {        
        var text = this.pageContent.find(content => content.contentName === 'Address Restriction Found');
        return text ? text.content : '';
      }
    },
    jointBorrower: {
      get() {
        return this.isJointApplication;
      }
    },
    address1: {
      get() {
        return this.jointAddress.address1;
      },
      set(value) {
        this.updateApplicantAddress1({ isPrimary: false, isCurrentAddress: true, address1: value });
      }
    },
    address2: {
      get() {
        return this.jointAddress.address2;
      },
      set(value) {
        this.updateApplicantAddress2({ isPrimary: false, isCurrentAddress: true, address2: value });
      }
    },
    city: {
      get() {
        return this.jointAddress.city;
      },
      set(value) {
        this.updateApplicantCity({ isPrimary: false, isCurrentAddress: true, city: value });
      }
    },
    state: {
      get() {
        return this.jointAddress.state;
      },
      set(value) {
        this.updateApplicantState({ isPrimary: false, isCurrentAddress: true, state: value });
      }
    },
    zipCode: {
      get() {
        return this.jointAddress.zipCode;
      },
      set(value) {
        this.updateApplicantZipCode({ isPrimary: false, isCurrentAddress: true, zipCode: value });
      }
    },
    yearsAtAddress: {
      get() {
        return this.jointAddress.yearsAtAddress;
      },
      set(value) {
        this.updateYearsAtAddress({ isPrimary: false, isCurrentAddress: true, yearsAtAddress: value});
      }
    },
    monthsAtAddress: {
      get() {
        return this.jointAddress.monthsAtAddress;
      },
      set(value) {
        this.updateMonthsAtAddress({ isPrimary: false, isCurrentAddress: true, monthsAtAddress: value});
      }
    },
    housingType: {
      get() {
        return this.jointAddress.housingType;
      },
      set(value) {
        this.updateApplicantHousingType({ isPrimary: false, isCurrentAddress: true, housingType: value });
      }
    },
    homeValue: {
      get() {
        return this.jointAddress.homeValue;
      },
      set(value) {
        this.updateApplicantHomeValue({ isPrimary: false, isCurrentAddress: true, homeValue: value });
      }
    },
    monthlyPayment: {
      get() {
        return this.jointAddress.monthlyPayment;
      },
      set(value) {
        this.updateApplicantMonthlyPayment({ isPrimary: false, isCurrentAddress: true, monthlyPayment: value });
      }
    },
    phoneType: {
      get() {
        return this.jointPhone.phoneType;
      },
      set(value) {
        this.updatePhoneType({ isPrimary: false, phoneType: value });
      }
    },
    phoneTypeText: {
      get() {
        return this.jointPhone.phoneTypeText
      },
      set(value) {
        this.updatePhoneTypeText({ isPrimary: false, phoneTypeText: value} )
      }
    },
    phoneNumber: {
      get() {
        return this.jointPhone.phoneNumber;
      },
      set(value) {
        this.updateApplicantPhone({ isPrimary: false,  phoneNumber: value });
      }
    },
    emailType: {
      get() {
        return this.jointEmail.emailType;
      },
      set(value) {
        this.updateEmailType({ isPrimary: false, emailType: value });
      }
    },
    emailTypeText: {
      get() {
        return this.jointEmail.emailTypeText
      },
      set(value) {
        this.updateEmailTypeText({ isPrimary: false, emailTypeText: value})
      }
    },
    emailAddress: {
      get() {
        return this.jointEmail.emailAddress;
      },
      set(value) {
        this.updateApplicantEmail({ isPrimary: false,  emailAddress: value });
      }
    },
    preferredMethodOfContact: {
      get() {
        return this.jointApplicant.preferredMethodOfContact;
      },
      set(value) {
        this.updatePreferredMethodOfContact({ isPrimary: false, preferredMethodOfContact: value });
      }
    },
    separateMailingAddress: {
      get() {
        return this.jointMailingAddress.separateMailingAddress;
      },
      set(value) {
        this.updateApplicantMailingSeparate({ isPrimary: false, separateMailingAddress: value });
      }
    },
    mailingAddress1: {
      get() {
        return this.jointMailingAddress.address1;
      },
      set(value) {
        this.updateApplicantMailingAddress1({ isPrimary: false, address1: value });
      }
    },
    mailingAddress2: {
      get() {
        return this.jointMailingAddress.address2;
      },
      set(value) {
        this.updateApplicantMailingAddress2({ isPrimary: false, address2: value });
      }
    },
    mailingCity: {
      get() {
        return this.jointMailingAddress.city;
      },
      set(value) {
        this.updateApplicantMailingCity({ isPrimary: false, isCurrentAddress: true, city: value });
      }
    },
    mailingState: {
      get() {
        return this.jointMailingAddress.state;
      },
      set(value) {
        this.updateApplicantMailingState({ isPrimary: false, isCurrentAddress: true, state: value });
      }
    },
    mailingZipCode: {
      get() {
        return this.jointMailingAddress.zipCode;
      },
      set(value) {
        this.updateApplicantMailingZipCode({ isPrimary: false, isCurrentAddress: true, zipCode: value });
      }
    },
    additionalAddressRequired() {
      console.log(this.minimumAddressHistoryMonths)
      console.log(this.jointAddressHistoryInMonths)
      if(this.jointAddressHistoryInMonths < this.minimumAddressHistoryMonths) {
        if(this.previousAddresses.length == 0
          && (this.monthsAtAddress != '' && this.yearsAtAddress != '')) {
          return true;
        } else if(this.previousAddresses.length > 0
          && (this.previousAddresses[this.previousAddresses.length-1].monthsAtAddress != '' && this.previousAddresses[this.previousAddresses.length-1].yearsAtAddress  != '')) {
          return true;
        }
      }
      return false;
    },
    eligibilityId: {
      get() {
        return this.jointEligibilityId;
      },
      set(value){
        this.updateEligiblityId({ isPrimary: false, eligibilityId: value });
      }
    },
    editedIdentificationInfo: {
      get() {
        return this.$store.state.editedIdentificationInfo;
      },
      set(value) {
        this.updateEditedIdentificationInfo(value);
      }
    },
    editedContactInfo: {
      get() {
          return this.$store.state.editedContactInfo;
      },
      set(value) {
          this.updateEditedContactInfo(value);
      }
    },
    contactInfoChanged: {
      get() {
        var prevJointAddress = this.currentState.jointAddress;
        console.log("prev joint address", prevJointAddress);
        console.log("joint address", this.jointAddress);
        if(prevJointAddress.address1 !== this.jointAddress.address1
          || prevJointAddress.address2 !== this.jointAddress.address2
          || prevJointAddress.city !== this.jointAddress.city
          || prevJointAddress.state !== this.jointAddress.state
          || prevJointAddress.zipCode !== this.jointAddress.zipCode) {
            return true;
        }
      }
    }
  },
  methods: {
    ...mapMutations([
      "updateApplicantAddress1",
      "updateApplicantAddress2",
      "updateApplicantCity",
      "updateApplicantState",
      "updateApplicantZipCode",
      "updateYearsAtAddress",
      "updateMonthsAtAddress",
      "updateApplicantHousingType",
      "updateApplicantHomeValue",
      "updateApplicantMonthlyPayment",
      "updatePhoneType",
      "updatePhoneTypeText",
      "updateApplicantPhone",
      "updateEmailType",
      "updateEmailTypeText",
      "updateApplicantEmail",
      "updatePreferredMethodOfContact",
      "updateApplicantMailingSeparate",
      "updateApplicantMailingAddress1",
      "updateApplicantMailingAddress2",
      "updateApplicantMailingCity",
      "updateApplicantMailingState",
      "updateApplicantMailingZipCode",
      "updateEligiblityId",
      'updateEditedIdentificationInfo',
      "updateEditedContactInfo"
    ]),
    ...mapActions([        
        "verifyEligibility",
        "blocklistCheck",
        "ipDistanceCheck",
        "addressCheck"
    ]),
    updateApplicantPhoneType(type) {
      this.phoneType = type.value;
      this.phoneTypeText = type.name;
    },
    updateApplicantEmailType(type) {
      this.emailType = type.value;
      this.emailTypeText = type.name;
    },
    submit: async function()  {
      this.$v.$touch();
      if(this.$v.$invalid) {
        this.$refs.errorModalRef.show();
      } else {
        this.processing = true;
        if(this.blockListCheckRequired) {
          let blocklistReq = {
            email: this.emailAddress,
            phone: this.phoneNumber,
            ssn: this.jointApplicant.taxId
          }  
          let blocklistCheck = await this.blocklistCheck(blocklistReq);
          if(!blocklistCheck) {
            this.processing = false;
            this.$refs.blocklistCheckRef.show();
            return;
          }
        }
        
        if(this.additionalAddressRequired) {
          this.processing = false;
          this.$refs.errorModalRef.show();
        } else {
          let eligibleParams = {};
          eligibleParams.address1 = this.address1;
          eligibleParams.city = this.city;
          eligibleParams.state = this.state;
          eligibleParams.zipCode = this.zipCode;
          eligibleParams.isResidence = true;
          let response = await this.verifyEligibility(eligibleParams);
          if(response) {
            let isEligible = response.isEligible;
            if(isEligible) {
              console.info('member is eligible, do nothing later');
              this.eligibilityId = response.eligibilityId;
            } else {
              console.info('member is not eligible, check later');
              if(this.editedIdentificationInfo) {
                this.$router.push("jointotherinformation");
              } else if(this.editMode && this.contactInfoChanged) {
                // an edit occurred and user is not eligible with the edited info,
                this.$refs.moreInfoRequiredModalRef.show();
                return;
              }
            }
          } else {
            console.info('error checking eligibility, check later');
            if(this.editedIdentificationInfo) {
              this.$router.push("jointotherinformation");
            } else if(this.editMode && this.contactInfoChanged) {
              // an edit occurred and user is not eligible with the edited info,
              this.$refs.moreInfoRequiredModalRef.show();
              return;
            }
          }          
          this.processing = false;

          if(this.editedIdentificationInfo) {
            this.$router.push("jointotherinformation");
          } else if(this.editMode) {
            this.$router.push("review");
          } else {
            this.$router.push("jointotherinformation");
          }
        }        
      }
    },
    gotoOtherInfo: async function() {
      console.info('continue to other info page, we are doing a multi-page edit');
      this.processing = false;
      this.editedContactInfo = true;
      this.$router.push("jointotherinformation");
    },
    cancel: function () {
      this.processing = false;
      this.$refs.moreInfoRequiredModalRef.hide();
    },
    back() {
      if(this.editedIdentificationInfo) {
        this.$router.push("jointidentification");
      } else if(this.editMode) {
        this.$store.replaceState(this.currentState);
        this.$router.push("review");
      } else {
        this.$router.push('jointidentification');
      }
    },
    addAdditionalHousing() {
      let previousAddress = {};
      previousAddress.address1 = '';
      previousAddress.city = '';
      previousAddress.state = '';
      previousAddress.zipCode = '';
      previousAddress.yearsAtAddress = '';
      previousAddress.monthsAtAddress = '';
      this.previousAddresses.push(previousAddress);
      this.$v.monthsAtAddress.$reset();
      this.$v.yearsAtAddress.$reset();
    },
    removeAdditionalHousing(index) {
      this.previousAddresses.splice(index, 1);
    },
  },
  validations: {
    address1: {
      required
    }, 
    city: {
      required
    },
    state: {
      required
    },
    zipCode: {
      required,
      zipCodeValidation (value) {
        return zipCodeValidation(this.state)(value);
      },
      minLength: minLength(5)
    },
    monthsAtAddress: {
      required,
      maxValue: maxValue(11),
      minValue: minValue(0),
    },
    yearsAtAddress: {
      required,
      minValue: minValue(0),
    },
    housingType: {
      required,
    },
    monthlyPayment: {
      required,
    },
    homeValue: {
      required: requiredIf(function () {
        return this.housingType === 'O';
      }),
    },
    phoneNumber: {
      required,
      minLength: minLength(13) // The value comes in with the mask so it's 13
    },
    phoneType: {
      required,
    },
    emailAddress: {
      required,
      email
    },
    emailType: {
      required,
    },
    preferredMethodOfContact: {
      required,
    },
    separateMailingAddress: {
      required,
    },
    mailingAddress1: {
      required: requiredIf(function () {
        return this.separateMailingAddress === true;
      }),      
    }, 
    mailingCity: {
      required: requiredIf(function () {
        return this.separateMailingAddress === true;
      }),      
    },
    mailingState: {
      required: requiredIf(function () {
        return this.separateMailingAddress === true;
      }),       
    },
    mailingZipCode: {
      required: requiredIf(function () {
        return this.separateMailingAddress === true;
      }),
      zipCodeValidation (value) {
        return zipCodeValidation(this.mailingState)(value);
      },
      minLength: minLength(5)
    },
    previousAddresses: {
      $each: {
        address1: {
          required,
          alphaNum
        },
        city: {
          required,
          alphaDash
        },
        state: {
          required,
        },
        zipCode: {
          required,
          zipCodeValidation (value, currItem) {
            return zipCodeValidation(currItem.state)(value);
          },
          minLength: minLength(5)
        },
        monthsAtAddress: {
          required,
          maxValue: maxValue(11),
          minValue: minValue(0),
        },
        yearsAtAddress: {
          required,
          minValue: minValue(0),
        },
      }
    }
  }
};
</script>